import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function StallApplication() {
  const { ptaId } = useParams();
  const [loading, setLoading] = useState(false);
  const [ptaInfo, setPtaInfo] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [formData, setFormData] = useState({
    businessName: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    description: "",
    eventId: "",
    keepForFuture: false,
  });

  useEffect(() => {
    if (ptaId) {
      fetchPTAInfo();
      fetchEvents();
      fetchSettings();
    }
  }, [ptaId]);

  const fetchPTAInfo = async () => {
    const { data, error } = await supabase
      .from("ptas")
      .select("name, logo_url")
      .eq("id", ptaId)
      .single();

    if (!error && data) {
      setPtaInfo(data);
    }
  };

  const fetchEvents = async () => {
    const { data } = await supabase
      .from("events")
      .select("*")
      .eq("pta_id", ptaId)
      .eq("status", "active")
      .gte("event_date", new Date().toISOString())
      .order("event_date", { ascending: true });

    if (data) {
      setEvents(data);
    }
  };

  const fetchSettings = async () => {
    const { data } = await supabase
      .from("stall_application_settings")
      .select("*")
      .eq("pta_id", ptaId)
      .single();

    if (data) {
      setSettings(data);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const selectedEvent = events.find(e => e.id === formData.eventId);
      const autoDeleteDate = !formData.keepForFuture && selectedEvent
        ? new Date(new Date(selectedEvent.event_date).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        : null;

      const { error } = await supabase
        .from("stall_applications")
        .insert({
          pta_id: ptaId,
          event_id: formData.eventId,
          business_name: formData.businessName,
          contact_name: formData.contactName,
          contact_email: formData.contactEmail,
          contact_phone: formData.contactPhone,
          description: formData.description,
          payment_amount: 25.00,
          keep_for_future: formData.keepForFuture,
          auto_delete_date: autoDeleteDate,
        });

      if (error) throw error;

      toast.success("Application submitted successfully!");
      setFormData({
        businessName: "",
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        description: "",
        eventId: "",
        keepForFuture: false,
      });
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  if (!settings?.accept_applications) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            {ptaInfo?.logo_url && (
              <img src={ptaInfo.logo_url} alt={ptaInfo.name} className="h-20 mx-auto mb-4" />
            )}
            <CardTitle>Applications Closed</CardTitle>
            <CardDescription>
              {ptaInfo?.name || "This PTA"} is not currently accepting stall applications.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 py-12">
      <Card className="max-w-2xl mx-auto">
        <CardHeader className="text-center">
          {ptaInfo?.logo_url && (
            <img src={ptaInfo.logo_url} alt={ptaInfo.name} className="h-20 mx-auto mb-4" />
          )}
          <CardTitle>Stall Space Application</CardTitle>
          <CardDescription>
            Apply for a stall space at {ptaInfo?.name || "our"} upcoming events
          </CardDescription>
        </CardHeader>
        <CardContent>
          {settings?.prohibited_items && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <h4 className="font-semibold mb-2 text-destructive">Prohibited Items</h4>
              <p className="text-sm text-muted-foreground">{settings.prohibited_items}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="eventId">Select Event *</Label>
              <Select
                value={formData.eventId}
                onValueChange={(value) => setFormData({ ...formData, eventId: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose an event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name} - {new Date(event.event_date).toLocaleDateString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessName">Business Name *</Label>
              <Input
                id="businessName"
                value={formData.businessName}
                onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactName">Contact Name *</Label>
              <Input
                id="contactName"
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactEmail">Email *</Label>
              <Input
                id="contactEmail"
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactPhone">Phone *</Label>
              <Input
                id="contactPhone"
                type="tel"
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">What will you be selling? *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                required
              />
            </div>

            <div className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="keepForFuture">Keep my details for future events</Label>
                <p className="text-sm text-muted-foreground">
                  We'll save your business information for easier application to future events
                </p>
              </div>
              <Switch
                id="keepForFuture"
                checked={formData.keepForFuture}
                onCheckedChange={(checked) => setFormData({ ...formData, keepForFuture: checked })}
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit Application
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
